import React, { useState, useEffect, useCallback, useRef } from "react";
import { Table, Button, Card, Badge, Avatar, Typography, Tooltip, Empty, Modal, DatePicker } from "antd";
import type { TablePaginationConfig } from "antd";
import { useNavigate } from "react-router-dom";
import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import {
    ReloadOutlined,
    UserOutlined,
    PhoneOutlined,
    PlayCircleOutlined,
    DownloadOutlined,
    ClockCircleOutlined,
    FireOutlined,
    SyncOutlined,
} from "@ant-design/icons";
import moment from "moment";

import StitchLayout from "../../components/Crm/CrmLayout";
import OzonetelFilters from "./OzonetelFilters";
import PriorityBadge from "../../components/Crm/PriorityBadge";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface OzonetelCall {
    _id: string;
    UCID: string;

    Agent: string;
    AgentStatus: string;

    E164: string;
    DID: string;

    // Call outcome
    Status: string;
    HangupBy: string;
    Type: string;

    // Campaign
    CampaignName: string;

    // Timing
    StartTime: string;
    EndTime: string;

    CallDuration: string;
    Duration: string;
    HandlingTime: string;

    WrapUpDuration: string;

    AudioFile: string | null;

    ivr_feedback?: {
        OptionResponse?: 'Satisfied' | 'Not_Satisfied' | 'No_input';
    };

    whatsapp_feedback?: {
        rating?: 'Good' | 'Average' | 'Poor' | string;
        received_at?: string;
        message_id?: string;
    } | null;

    order_details?: Array<{ priorityStatus?: number;[key: string]: any }>;
    freshdesk_ticket_id?: number | null;
}

const WHATSAPP_PILL_STYLES: Record<string, { bg: string; color: string; border: string; emoji: string }> = {
    Good: { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0', emoji: '😊' },
    Average: { bg: '#fef9c3', color: '#a16207', border: '#fde68a', emoji: '😐' },
    Poor: { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca', emoji: '😞' },
};

//SessionStorage key for persisting call intelligence filters across tab navigation
const FILTER_SESSION_KEY = 'call_intelligence_filters';

//Build today's date boundaries for the default filter
const TODAY_START = moment().startOf('day').format('YYYY-MM-DDTHH:mm:ss');
const TODAY_END = moment().endOf('day').format('YYYY-MM-DDTHH:mm:ss');

//Default filter object pre-scoped to today
interface OzonetelFiltersType {
    Agent: string;
    AgentStatus: string;
    E164: string;
    CampaignName: string;
    HangupBy: string;
    Status: string;
    Type: string;
    UCID: string;
    start_date: string;
    end_date: string;
    ivr_feedback?: string;
    whatsapp_feedback?: string;
}

const DEFAULT_FILTERS: OzonetelFiltersType = {
    Agent: "",
    AgentStatus: "",
    E164: "",
    CampaignName: "",
    HangupBy: "",
    Status: "",
    Type: "",
    UCID: "",
    start_date: TODAY_START,
    end_date: TODAY_END,
    ivr_feedback: undefined,
    whatsapp_feedback: undefined,
};

//Read persisted filters and activeQuick from sessionStorage, returns null if none
const readSessionFilters = (): { filters: typeof DEFAULT_FILTERS; activeQuick: string | null } | null => {
    try {
        const raw = sessionStorage.getItem(FILTER_SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

//Write filters and activeQuick into sessionStorage
const writeSessionFilters = (filters: typeof DEFAULT_FILTERS, activeQuick: string | null) => {
    try {
        sessionStorage.setItem(FILTER_SESSION_KEY, JSON.stringify({ filters, activeQuick }));
    } catch {
        //Silently ignore sessionStorage write failures
    }
};

const OzonetelCallTrack = () => {
    const navigate = useNavigate();
    const [calls, setCalls] = useState<OzonetelCall[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        pages: 1,
        per_page: 10,
        total: 0,
    });
    const [filters, setFilters] = useState<OzonetelFiltersType>(() => {
        const saved = readSessionFilters();
        return saved ? saved.filters : DEFAULT_FILTERS;
    });
    const [initialActiveQuick, setInitialActiveQuick] = useState<string | null>(() => {
        const saved = readSessionFilters();
        return saved ? saved.activeQuick : null;
    });
    const [fetchTrigger, setFetchTrigger] = useState(0);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadRange, setDownloadRange] = useState<any[]>([]);

    const [queueCount, setQueueCount] = useState<number | null>(null);
    const [queuePulse, setQueuePulse] = useState(false);
    const queueIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const fetchQueueCount = useCallback(async () => {
        try {
            const res = await fetch(apis.OZONETEL_QUEUE_COUNT);
            const json = await res.json();
            if (json.status) {
                setQueueCount(prev => {
                    if (prev !== json.count) {
                        setQueuePulse(true);
                        setTimeout(() => setQueuePulse(false), 600);
                    }
                    return json.count;
                });
            }
        } catch {
            // silently ignore network errors for queue count
        }
    }, []);

    useEffect(() => {
        fetchQueueCount();
        queueIntervalRef.current = setInterval(fetchQueueCount, 2000);
        return () => {
            if (queueIntervalRef.current) clearInterval(queueIntervalRef.current);
        };
    }, [fetchQueueCount]);

    const [filterOptions, setFilterOptions] = useState<any>({
        Agent: [],
        AgentStatus: [],
        CampaignName: [],
        HangupBy: [],
        Status: [],
        Type: [],
        ivr_feedback: [],
        whatsapp_feedback: [],
    });

    const fetchFilterOptions = async () => {
        try {
            const response = await axiosInstance.get(apis.OZONETEL_LIVE_REPORT_FILTERS);
            const data = response.data;
            if (data.status) {
                setFilterOptions((prev: any) => ({
                    ...prev,
                    ...data.data,
                    ivr_feedback: data.data.ivr_feedback || [],
                    whatsapp_feedback: data.data.whatsapp_feedback || [],
                }));
            }
        } catch (error) {
            console.error("Error fetching filter options:", error);
        }
    };

    const fetchCalls = async (page = 1, per_page = 10, currentFilters = filters) => {
        setLoading(true);
        try {
            // Build a plain object for API filters
            let filtersToSend: any = { ...currentFilters };
            if (filtersToSend.ivr_feedback) {
                filtersToSend["ivr_feedback.OptionResponse"] = filtersToSend.ivr_feedback;
                delete filtersToSend.ivr_feedback;
            }
            const response = await axiosInstance.post(
                `${apis.OZONETEL_LIVE_REPORT}?page=${page}&per_page=${per_page}`,
                { filters: filtersToSend }
            );
            const data = response.data;
            if (data.data) {
                setCalls(data.data);
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error("Error fetching call data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFilterOptions();
    }, []);

    useEffect(() => {
        fetchCalls(pagination.page, pagination.per_page, filters);
    }, [fetchTrigger]);

    const handleTableChange = (
        pagination: TablePaginationConfig,
    ) => {
        fetchCalls(pagination.current, pagination.pageSize, filters);
    };

    //Apply incoming filters from the filter panel, persist to session, and trigger data fetch
    const handleFilter = useCallback((newFilters: any, activeQuick: string | null = null) => {
        setFilters(newFilters);
        setInitialActiveQuick(activeQuick);
        writeSessionFilters(newFilters, activeQuick);
        setFetchTrigger(c => c + 1);
    }, []);

    //Reset filters to defaults and clear sessionStorage
    const handleReset = useCallback(() => {
        setFilters(DEFAULT_FILTERS);
        setInitialActiveQuick(null);
        sessionStorage.removeItem(FILTER_SESSION_KEY);
        setFetchTrigger(c => c + 1);
    }, []);

    //Fetch all records for selected date range and download as CSV
    const handleDownloadConfirm = async () => {
        if (!downloadRange || !downloadRange[0] || !downloadRange[1]) return;
        setDownloading(true);
        try {
            const response = await axiosInstance.post(
                `${apis.OZONETEL_LIVE_REPORT_DOWNLOAD}`,
                {
                    filters: {
                        ...filters,
                        //Always use start of day and end of day for download date boundaries
                        start_date: downloadRange[0].startOf('day').format("YYYY-MM-DDTHH:mm:ss"),
                        end_date: downloadRange[1].endOf('day').format("YYYY-MM-DDTHH:mm:ss"),
                    }
                }
            );
            const records: any[] = response.data?.data || [];
            const headers = ['UCID', 'Agent', 'AgentStatus', 'E164', 'CampaignName', 'Status', 'StartTime', 'CallDuration', 'HangupBy', 'Type'];
            const rows = records.map(c => [
                c.UCID, c.Agent, c.AgentStatus, c.E164,
                c.CampaignName, c.Status,
                c.StartTime ? moment(c.StartTime).format('YYYY-MM-DD HH:mm:ss') : '',
                c.CallDuration, c.HangupBy, c.Type,
            ]);
            const csv = [headers, ...rows].map(r => r.map((v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ozonetel_calls_${downloadRange[0].format('YYYYMMDD')}_to_${downloadRange[1].format('YYYYMMDD')}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            setDownloadModalVisible(false);
        } catch (err) {
            console.error('Download failed', err);
        } finally {
            setDownloading(false);
        }
    };

    const handleUcidClick = (ucid: string) => {
        navigate(`/crm/ozonetel-call-track/${ucid}`);
    };

    // Computes call ageing from StartTime to now — same color scale as FreshdeskTickets
    const getCallAgeing = (startTime: string) => {
        if (!startTime) return null;
        const now = moment();
        const start = moment(startTime);
        const diffMinutes = now.diff(start, 'minutes');
        const diffHours = now.diff(start, 'hours');
        const diffDays = now.diff(start, 'days');

        let label: string, color: string, bg: string, border: string, dot: string;

        if (diffMinutes < 60) {
            label = `${diffMinutes}m ago`;
            color = '#15803d'; bg = '#f0fdf4'; border = '#bbf7d0'; dot = '#22c55e';
        } else if (diffHours < 24) {
            label = `${diffHours}h ago`;
            color = '#0369a1'; bg = '#f0f9ff'; border = '#bae6fd'; dot = '#38bdf8';
        } else if (diffDays <= 3) {
            label = `${diffDays}d ${diffHours % 24}h`;
            color = '#1d4ed8'; bg = '#eff6ff'; border = '#bfdbfe'; dot = '#60a5fa';
        } else if (diffDays <= 7) {
            label = `${diffDays} days`;
            color = '#b45309'; bg = '#fffbeb'; border = '#fde68a'; dot = '#f59e0b';
        } else if (diffDays <= 14) {
            label = `${diffDays} days`;
            color = '#c2410c'; bg = '#fff7ed'; border = '#fed7aa'; dot = '#f97316';
        } else {
            label = `${diffDays} days`;
            color = '#b91c1c'; bg = '#fef2f2'; border = '#fecaca'; dot = '#ef4444';
        }

        return { label, color, bg, border, dot, diffDays };
    };

    const columns = [
        {
            title: "UCID",
            dataIndex: "UCID",
            key: "UCID",
            width: 140,
            render: (text: string, record: any) => {
                const orderDetails: any[] = record.order_details || [];
                const minPriority = orderDetails.length > 0
                    ? Math.min(...orderDetails.map((o: any) => o.priorityStatus ?? 2))
                    : 2;
                return (
                    <div className="flex flex-col gap-0.5">
                        <Tooltip title="View Call Analysis">
                            <span
                                onClick={() => handleUcidClick(text)}
                                className="font-medium text-blue-600 cursor-pointer hover:text-blue-800 transition-colors font-mono text-xs"
                            >
                                {text}
                            </span>
                        </Tooltip>
                        {(minPriority === 0 || minPriority === 1) && (
                            <PriorityBadge priorityStatus={minPriority} />
                        )}
                    </div>
                );
            },
        },
        {
            title: "Agent",
            dataIndex: "Agent",
            key: "Agent",
            width: 160,
            render: (text: string, record: any) => (
                <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                        {text && <Avatar size="small" style={{ backgroundColor: '#f56a00', verticalAlign: 'middle', flexShrink: 0 }} gap={1}>
                            {text ? text.charAt(0).toUpperCase() : <UserOutlined />}
                        </Avatar>}
                        <span className="font-medium text-gray-700">{text || ""}</span>
                    </div>
                    {record.freshdesk_ticket_id && (
                        <Tooltip title="View Ticket">
                            <a
                                href={`/crm/freshdesk-tickets/${record.freshdesk_ticket_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-[11px] text-purple-600 cursor-pointer hover:underline flex items-center gap-1 w-fit mt-0.5"
                                style={{ marginLeft: text ? '32px' : '0' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                🎫 #{record.freshdesk_ticket_id}
                            </a>
                        </Tooltip>
                    )}
                </div>
            ),
        },
        {
            title: "Agent Status",
            dataIndex: "AgentStatus",
            key: "AgentStatus",
            width: 120,
            render: (status: string) => {
                //Skip badge rendering entirely when status is absent
                if (!status) return <span className="text-gray-300">—</span>;
                const isAnswered = status?.toLowerCase() === "answered";
                const isNotAnswered = status?.toLowerCase() === "not_answered" || status?.toLowerCase() === "notanswered";

                return (
                    <Badge
                        status={isAnswered ? "success" : isNotAnswered ? "warning" : "default"}
                        text={<span className="capitalize text-gray-600">{status}</span>}
                    />
                );
            },
        },
        {
            title: "Type",
            dataIndex: "Type",
            key: "Type",
            width: 90,
            render: (type: string) => {
                if (!type) return <span className="text-gray-300">—</span>;
                const t = type?.toLowerCase();
                const style: React.CSSProperties = {
                    display: 'inline-block',
                    padding: '1px 8px',
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    ...(t === 'manual'
                        ? { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }
                        : t === 'inbound'
                            ? { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }
                            : t === 'ivr'
                                ? { background: '#faf5ff', color: '#7e22ce', border: '1px solid #e9d5ff' }
                                : { background: '#f9fafb', color: '#6b7280', border: '1px solid #e5e7eb' }),
                };
                return <span style={style}>{type}</span>;
            },
        },
        {
            title: "E164",
            dataIndex: "E164",
            key: "E164",
            width: 140,
            render: (text: string) => (
                <div className="flex items-center gap-2 text-gray-600">
                    <PhoneOutlined className="text-gray-400" />
                    <span className="font-mono text-xs">{text}</span>
                </div>
            ),
        },
        {
            title: "Campaign",
            dataIndex: "CampaignName",
            key: "CampaignName",
            width: 160,
            ellipsis: true,
            render: (text: string) => <Tooltip title={text}><span className="text-gray-600">{text}</span></Tooltip>,
        },
        {
            title: "Status",
            dataIndex: "Status",
            key: "Status",
            width: 110,
            render: (_: string, record: any) => {
                const status = record.Status;
                if (!status) return <span className="text-gray-300">—</span>;
                const isAnswered = status?.toLowerCase() === "answered";
                const isNotAnswered = status?.toLowerCase() === "notanswered" || status?.toLowerCase() === "not_answered";

                const styles: React.CSSProperties = {
                    display: 'inline-block',
                    padding: '2px 10px',
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    ...(isAnswered
                        ? { background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' }
                        : isNotAnswered
                            ? { background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }
                            : { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }),
                };

                // Feedback strip style
                const feedback = record.ivr_feedback?.OptionResponse;
                const waFeedback = record.whatsapp_feedback?.rating;
                const waStyle = WHATSAPP_PILL_STYLES[waFeedback ?? ''] ?? {
                    bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db', emoji: '💬',
                };
                const getFeedbackStyle = (feedback: string): React.CSSProperties => {
                    const baseStyle: React.CSSProperties = {
                        display: 'inline-block',
                        padding: '1px 8px',
                        borderRadius: 12,
                        fontSize: 10,
                        fontWeight: 500,
                        marginTop: 3,
                        letterSpacing: '0.03em',
                        textTransform: 'capitalize',
                        minWidth: 0,
                        maxWidth: '100%',
                    };
                    if (feedback === 'Satisfied') {
                        return { ...baseStyle, background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' };
                    } else if (feedback === 'Not_Satisfied') {
                        return { ...baseStyle, background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' };
                    } else if (feedback === 'No_input') {
                        return { ...baseStyle, background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db' };
                    }
                    return baseStyle;
                };

                return (
                    <div className="flex flex-col gap-0.5 items-center w-full">
                        <span style={styles}>{status}</span>
                        {feedback && (
                            <span
                                style={{
                                    ...getFeedbackStyle(feedback),
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    maxWidth: 100,
                                    minHeight: 22,
                                    padding: '0 10px',
                                    fontSize: 11,
                                    marginTop: 4,
                                }}
                            >
                                {feedback.replace('_', ' ')}
                            </span>
                        )}
                        {/* Post-call WhatsApp reply, when the customer answered the prompt */}
                        {waFeedback && (
                            <Tooltip title={`Customer replied "${waFeedback}" to the post-call WhatsApp prompt`}>
                                <span
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: 4,
                                        width: '100%',
                                        maxWidth: 100,
                                        minHeight: 22,
                                        padding: '0 8px',
                                        marginTop: 4,
                                        borderRadius: 12,
                                        fontSize: 11,
                                        fontWeight: 500,
                                        background: waStyle.bg,
                                        color: waStyle.color,
                                        border: `1px solid ${waStyle.border}`,
                                    }}
                                >
                                    <span style={{ fontSize: 10 }}>{waStyle.emoji}</span>
                                    {waFeedback}
                                </span>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Time",
            dataIndex: "StartTime",
            key: "StartTime",
            width: 150,
            render: (text: string) => {
                const ageing = getCallAgeing(text);
                return (
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 text-gray-600">
                            <ClockCircleOutlined className="text-gray-400 flex-shrink-0" style={{ fontSize: 11 }} />
                            <span className="text-xs whitespace-nowrap text-gray-500">
                                {moment(text).format("MMM DD, YYYY HH:mm")}
                            </span>
                        </div>
                        {ageing && (
                            <div
                                className="inline-flex items-center gap-1 self-start"
                                style={{
                                    background: ageing.bg,
                                    border: `1px solid ${ageing.border}`,
                                    borderRadius: 20,
                                    padding: '2px 8px',
                                    maxWidth: 'fit-content',
                                }}
                            >
                                <span
                                    style={{
                                        display: 'inline-block',
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        background: ageing.dot,
                                        flexShrink: 0,
                                        boxShadow: ageing.diffDays >= 7 ? `0 0 0 2px ${ageing.border}` : undefined,
                                    }}
                                />
                                {ageing.diffDays >= 7 && (
                                    <FireOutlined style={{ fontSize: 10, color: ageing.color }} />
                                )}
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: ageing.color,
                                        whiteSpace: 'nowrap',
                                        letterSpacing: '0.2px',
                                    }}
                                    title={`Call age: ${ageing.label}`}
                                >
                                    {ageing.label}
                                </span>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: (
                <div className="flex items-center gap-1">
                    <span>Timings</span>
                    <Tooltip
                        title={
                            <div style={{ fontSize: 12, lineHeight: '1.8' }}>
                                <div><span style={{ color: '#93c5fd' }}>📞 Call</span> — Total lifecycle (Ring + IVR + Talk)</div>
                                <div><span style={{ color: '#6ee7b7' }}>🗣 Talk</span> — Agent ↔ Customer talk time only</div>
                                <div><span style={{ color: '#fcd34d' }}>⏱ Handle</span> — Agent effort (Talk + Hold)</div>
                            </div>
                        }
                        overlayStyle={{ maxWidth: 260 }}
                    >
                        <span style={{ cursor: 'help', color: '#9ca3af', fontSize: 13 }}>ⓘ</span>
                    </Tooltip>
                </div>
            ),
            key: "timings",
            width: 180,
            render: (_: any, record: OzonetelCall) => (
                <div className="flex items-center gap-1 flex-wrap">
                    <Tooltip title="CallDuration — Total call lifecycle (Ring + IVR + Talk)">
                        <span className="inline-flex items-center gap-1 font-mono text-xs text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 whitespace-nowrap">
                            <span className="text-[9px] font-bold text-blue-400 uppercase">C</span>
                            {record.CallDuration || '—'}
                        </span>
                    </Tooltip>
                    <span className="text-gray-200 text-xs">|</span>
                    <Tooltip title="Duration — Talk time only (Agent ↔ Customer)">
                        <span className="inline-flex items-center gap-1 font-mono text-xs text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100 whitespace-nowrap">
                            <span className="text-[9px] font-bold text-emerald-400 uppercase">T</span>
                            {record.Duration || '—'}
                        </span>
                    </Tooltip>
                </div>
            ),
        },
        {
            title: "Recording",
            dataIndex: "AudioFile",
            key: "AudioFile",
            width: 80,
            align: 'center',
            render: (audioFile: string) => {
                if (audioFile) {
                    return (
                        <Tooltip title="Play Recording">
                            <Button
                                type="text"
                                shape="circle"
                                icon={<PlayCircleOutlined className="text-blue-600 text-lg" />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // You could render a quick player popover here or just let them open details
                                    window.open(audioFile, '_blank');
                                }}
                            />
                        </Tooltip>
                    );
                }
                return <span className="text-gray-300">-</span>;
            },
        },
    ];

    return (
        <StitchLayout>
            <div className="space-y-4 max-w-[1600px] mx-auto">
                {/*Compact page header with smaller icon and title */}
                <div className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-3">
                        {/*Smaller icon badge for the compact header */}
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-base">
                            <PhoneOutlined />
                        </div>
                        <div>
                            <Title level={5} style={{ marginBottom: 0, letterSpacing: '-0.3px' }}>Call Intelligence</Title>
                            <Text type="secondary" style={{ fontSize: 12 }}>Monitor and analyze agent interactions in real-time</Text>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 mr-2">
                        {/* Compact Queue Indicator */}
                        <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 border border-gray-100 rounded-lg shadow-sm">
                            <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 12, height: 12 }}>
                                <span style={{
                                    position: 'absolute',
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    background: queueCount !== null && queueCount > 0 ? '#fb923c' : '#4ade80',
                                    opacity: 0.25,
                                    animation: 'queuePingAnim 1.5s cubic-bezier(0,0,0.2,1) infinite',
                                }} />
                                <span style={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    background: queueCount !== null && queueCount > 0 ? '#f97316' : '#22c55e',
                                }} />
                            </span>
                            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Queue:</span>
                            <span
                                style={{
                                    fontSize: 14,
                                    fontWeight: 800,
                                    color: queueCount !== null && queueCount > 0 ? '#ea580c' : '#16a34a',
                                    minWidth: 20,
                                    textAlign: 'center',
                                    transition: 'transform 0.2s',
                                    transform: queuePulse ? 'scale(1.3)' : 'scale(1)',
                                    display: 'inline-block',
                                }}
                            >
                                {queueCount === null ? '—' : queueCount}
                            </span>
                            <Tooltip title="Live Updates (2s)">
                                <SyncOutlined spin style={{ fontSize: 10, color: '#60a5fa', marginLeft: 2 }} />
                            </Tooltip>
                        </div>

                        <div className="flex gap-2">
                            {/*Download Report opens modal with date range picker */}
                            <Button
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={() => setDownloadModalVisible(true)}
                            >
                                Download Report
                            </Button>
                            <Button size="small" icon={<ReloadOutlined />} onClick={() => setFetchTrigger(c => c + 1)}>Refresh</Button>
                        </div>
                    </div>
                </div>

                {/* CSS keyframe for ping animation (injected once) */}
                <style>{`
                    @keyframes queuePingAnim {
                        0%   { transform: scale(1);   opacity: 0.25; }
                        70%  { transform: scale(2.2); opacity: 0;    }
                        100% { transform: scale(2.2); opacity: 0;    }
                    }
                `}</style>

                {/*Collapsible filters component with session-restored activeQuick */}
                <OzonetelFilters
                    onFilter={handleFilter}
                    onReset={handleReset}
                    loading={loading}
                    filterOptions={filterOptions}
                    initialValues={filters}
                    initialActiveQuick={initialActiveQuick}
                />

                {/*Call records table */}
                <Card bordered={false} className="shadow-sm rounded-xl overflow-hidden" bodyStyle={{ padding: 0 }}>
                    <Table
                        columns={columns as any}
                        dataSource={calls}
                        loading={loading}
                        rowKey="_id"
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.per_page,
                            total: pagination.total,
                            showSizeChanger: true,
                            showTotal: (total, range) => <span className="text-gray-500 mx-4">{`${range[0]}-${range[1]} of ${total} calls`}</span>,
                            className: "p-4"
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 'max-content' }}
                        size="middle"
                        locale={{
                            emptyText: <Empty description="No call records found matching your filters" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        }}
                    />
                </Card>
            </div>

            {/*Download report modal with date range selector */}
            <Modal
                title={<span className="flex items-center gap-2"><DownloadOutlined /> Download Report</span>}
                open={downloadModalVisible}
                onCancel={() => setDownloadModalVisible(false)}
                onOk={handleDownloadConfirm}
                okText={downloading ? "Downloading…" : "Download CSV"}
                okButtonProps={{ loading: downloading, icon: <DownloadOutlined /> }}
                cancelButtonProps={{ disabled: downloading }}
                width={420}
            >
                {/*Date range picker defaulting to today */}
                <div className="py-2">
                    <p className="text-sm text-gray-500 mb-3">Select a date range to download all matching call records.</p>
                    <RangePicker
                        style={{ width: '100%' }}
                        onChange={(vals) => setDownloadRange(vals ?? [])}
                        disabledDate={(d) => d && d.format('YYYY-MM-DD') > moment().format('YYYY-MM-DD')}
                    />
                </div>
            </Modal>
        </StitchLayout>
    );
};

export default OzonetelCallTrack;